import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";
import { DEMO_TIME_OPTIONS } from "@workspace/api-zod";

interface BookDemoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BookDemoModal({ open, onOpenChange }: BookDemoModalProps) {
  const [name, setName] = useState("");
  const [gymName, setGymName] = useState("");
  const [email, setEmail] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setName("");
    setGymName("");
    setEmail("");
    setPreferredTime("");
    setSubmitting(false);
    setSubmitted(false);
    setError(null);
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      setTimeout(resetForm, 300);
    }
    onOpenChange(open);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) { setError("Name is required."); return; }
    if (!gymName.trim()) { setError("Gym name is required."); return; }
    if (!email.trim()) { setError("Email is required."); return; }
    if (!preferredTime) { setError("Please select a preferred demo time."); return; }

    setSubmitting(true);
    try {
      const apiBase = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${apiBase}/api/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), gymName: gymName.trim(), email: email.trim(), preferredTime }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        return;
      }

      setSubmitted(true);
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="border-white/[0.08] bg-[#0d0d0d] text-white sm:rounded-xl overflow-hidden"
        style={{ maxWidth: 480 }}
      >
        {submitted ? (
          <div className="flex flex-col items-center text-center py-8 px-4 gap-4">
            <div className="w-14 h-14 rounded-full bg-emerald-400/10 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <DialogTitle
              className="text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.8rem", letterSpacing: "0.04em" }}
            >
              You're on the list.
            </DialogTitle>
            <p className="text-white/50 text-sm leading-relaxed" style={{ fontFamily: "'DM Mono', monospace" }}>
              We'll reach out to schedule your demo. Talk soon.
            </p>
            <Button
              onClick={() => handleOpenChange(false)}
              className="mt-2 bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
              style={{ fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", borderRadius: 4 }}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="pb-1">
              <DialogTitle
                className="text-white"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "2rem", letterSpacing: "0.04em", lineHeight: 1 }}
              >
                Book a Demo
              </DialogTitle>
              <DialogDescription className="text-white/40 text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>
                Tell us about your gym and we'll set up a personalized walkthrough.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-1">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="demo-name" className="text-white/70 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Mono', monospace" }}>
                  Your Name
                </Label>
                <Input
                  id="demo-name"
                  type="text"
                  placeholder="Marcus Rivera"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  required
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus-visible:ring-primary/50 focus-visible:border-primary/40"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="demo-gym" className="text-white/70 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Mono', monospace" }}>
                  Gym Name
                </Label>
                <Input
                  id="demo-gym"
                  type="text"
                  placeholder="Rivera Combat Academy"
                  value={gymName}
                  onChange={(e) => setGymName(e.target.value)}
                  maxLength={150}
                  required
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus-visible:ring-primary/50 focus-visible:border-primary/40"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="demo-email" className="text-white/70 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Mono', monospace" }}>
                  Email Address
                </Label>
                <Input
                  id="demo-email"
                  type="email"
                  placeholder="marcus@riveracombat.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus-visible:ring-primary/50 focus-visible:border-primary/40"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="demo-time" className="text-white/70 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Mono', monospace" }}>
                  Preferred Demo Time
                </Label>
                <div className="relative">
                  <select
                    id="demo-time"
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    required
                    style={{
                      width: "100%",
                      appearance: "none",
                      WebkitAppearance: "none",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 6,
                      color: preferredTime ? "#fff" : "rgba(255,255,255,0.2)",
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 14,
                      padding: "9px 36px 9px 12px",
                      outline: "none",
                      cursor: "pointer",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(var(--primary-rgb, 234 179 8), 0.4)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
                  >
                    <option value="" disabled style={{ background: "#0d0d0d", color: "rgba(255,255,255,0.4)" }}>
                      Select a time window…
                    </option>
                    {DEMO_TIME_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} style={{ background: "#0d0d0d", color: "#fff" }}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  <div
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      color: "rgba(255,255,255,0.3)",
                      fontSize: 10,
                    }}
                  >
                    ▼
                  </div>
                </div>
              </div>

              {error && (
                <p className="text-red-400 text-xs" style={{ fontFamily: "'DM Mono', monospace" }}>
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-lg shadow-primary/20 disabled:opacity-50 gap-2"
                style={{
                  fontFamily: "'DM Mono', monospace",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  borderRadius: 4,
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Request a Demo"
                )}
              </Button>

              <p className="text-center text-white/25 text-xs" style={{ fontFamily: "'DM Mono', monospace" }}>
                No commitment. We'll reach out within one business day.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
